import { Injectable, OnDestroy } from '@angular/core';

interface PendingNotificationSound {
  path: string;
  errorContext: string;
}

const KNOWN_NOTIFICATION_SOUNDS = [
  '/assets/notification/notificacion_1.mp3',
  '/assets/notification/notificacion_interna.mp3',
  '/assets/notification/mencion.ogg',
];

@Injectable({ providedIn: 'root' })
export class NotificationAudioService implements OnDestroy {
  private readonly players = new Map<string, HTMLAudioElement>();
  private readonly pendingSounds = new Map<string, PendingNotificationSound>();
  private unlocked = false;
  private priming = false;
  private listeningForUnlock = false;

  private readonly unlockOnUserGesture = () => this.unlockFromUserGesture();

  constructor() {
    if (typeof document === 'undefined' || typeof Audio === 'undefined') {
      return;
    }

    KNOWN_NOTIFICATION_SOUNDS.forEach((path) => this.getPlayer(path));
    this.unlocked = this.hasPriorUserActivation();
    this.registerUnlockListeners();
  }

  play(soundPath: string, errorContext = 'notificación'): void {
    const player = this.getPlayer(soundPath);
    if (!player) {
      return;
    }

    if (!this.unlocked || this.priming) {
      this.queueSound(soundPath, errorContext);
      return;
    }

    this.playPlayer(soundPath, errorContext, player);
  }

  ngOnDestroy(): void {
    this.removeUnlockListeners();
    this.players.forEach((player) => player.pause());
    this.players.clear();
    this.pendingSounds.clear();
  }

  private unlockFromUserGesture(): void {
    if (this.priming || this.players.size === 0) {
      return;
    }

    this.priming = true;
    this.unlocked = true;

    // Start each reusable audio element while a trusted gesture is active.
    // Muting the short priming playback avoids an audible click on the first action.
    const primingTasks = Array.from(this.players.values(), (player) =>
      this.primePlayer(player),
    );

    void Promise.all(primingTasks).then(() => {
      this.priming = false;
      if (!this.unlocked) {
        return;
      }

      this.removeUnlockListeners();
      const pending = Array.from(this.pendingSounds.values());
      this.pendingSounds.clear();
      pending.forEach(({ path, errorContext }) => this.play(path, errorContext));
    });
  }

  private primePlayer(player: HTMLAudioElement): Promise<void> {
    const wasMuted = player.muted;
    player.pause();
    player.muted = true;
    player.currentTime = 0;

    try {
      return player.play().then(() => {
        this.resetPlayerAfterPriming(player, wasMuted);
      }).catch((error: unknown) => {
        this.resetPlayerAfterPriming(player, wasMuted);
        if (this.isAutoplayBlocked(error)) {
          this.unlocked = false;
          this.registerUnlockListeners();
        }
      });
    } catch (error) {
      this.resetPlayerAfterPriming(player, wasMuted);
      if (this.isAutoplayBlocked(error)) {
        this.unlocked = false;
        this.registerUnlockListeners();
      }
      return Promise.resolve();
    }
  }

  private resetPlayerAfterPriming(
    player: HTMLAudioElement,
    wasMuted: boolean,
  ): void {
    player.pause();
    player.currentTime = 0;
    player.muted = wasMuted;
  }

  private playPlayer(
    soundPath: string,
    errorContext: string,
    player: HTMLAudioElement,
  ): void {
    // Keep a single cue audible when several messages arrive in a quick burst.
    if (!player.paused && !player.ended) {
      return;
    }

    try {
      player.currentTime = 0;
      player.muted = false;
      void player.play().catch((error: unknown) => {
        if (this.isAutoplayBlocked(error)) {
          this.unlocked = false;
          this.queueSound(soundPath, errorContext);
          this.registerUnlockListeners();
          return;
        }

        console.error(`Error al reproducir sonido de ${errorContext}:`, error);
      });
    } catch (error) {
      console.error(`Error al reproducir sonido de ${errorContext}:`, error);
    }
  }

  private getPlayer(soundPath: string): HTMLAudioElement | null {
    const existing = this.players.get(soundPath);
    if (existing) {
      return existing;
    }

    if (typeof Audio === 'undefined') {
      return null;
    }

    try {
      const player = this.createAudio(soundPath);
      player.preload = 'auto';
      player.load();
      this.players.set(soundPath, player);
      return player;
    } catch (error) {
      console.error('Error al preparar sonido de notificación:', error);
      return null;
    }
  }

  protected createAudio(soundPath: string): HTMLAudioElement {
    return new Audio(soundPath);
  }

  private queueSound(soundPath: string, errorContext: string): void {
    this.pendingSounds.set(soundPath, { path: soundPath, errorContext });
  }

  private hasPriorUserActivation(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const activation = (
      navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }
    ).userActivation;
    return activation?.hasBeenActive === true;
  }

  private isAutoplayBlocked(error: unknown): boolean {
    return typeof error === 'object' && error !== null &&
      'name' in error && (error as { name?: unknown }).name === 'NotAllowedError';
  }

  private registerUnlockListeners(): void {
    if (this.listeningForUnlock || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('pointerdown', this.unlockOnUserGesture, true);
    document.addEventListener('keydown', this.unlockOnUserGesture, true);
    this.listeningForUnlock = true;
  }

  private removeUnlockListeners(): void {
    if (!this.listeningForUnlock || typeof document === 'undefined') {
      return;
    }

    document.removeEventListener('pointerdown', this.unlockOnUserGesture, true);
    document.removeEventListener('keydown', this.unlockOnUserGesture, true);
    this.listeningForUnlock = false;
  }
}
